import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { Reveal } from '@/components/generals/reveal'

import {
    Drama,
    Ear,
    Mic,
    Sparkles,
    Star,
    Users
} from 'lucide-react'

interface Benefit {
    title: string
    text: string
    icon: LucideIcon
}

const BENEFITS: Benefit[] = [
    {
        title: 'ביטחון עצמי',
        text: 'לעמוד מול קבוצה, להשמיע קול ולהרגיש שמקשיבים לך. הביטחון שנבנה על הבמה ממשיך איתם לכיתה ולחיים.',
        icon: Star
    },
    {
        title: 'דיבור מול קהל',
        text: 'עבודה על קול, נשימה ודיקציה הופכת הצגת עבודה בכיתה או נאום בבר או בת מצווה למשהו מרגש ולא מפחיד.',
        icon: Mic
    },
    {
        title: 'דמיון ויצירתיות',
        text: 'אלתור, המצאת דמויות ובניית סיפורים מאמנים את הדמיון ואת היכולת לחשוב מחוץ לקופסה.',
        icon: Sparkles
    },
    {
        title: 'חברים ועבודת צוות',
        text: 'בתיאטרון אין סצנה בלי שותפים. לומדים להקשיב, לפרגן ולבנות משהו ביחד, ומכירים חברים חדשים בדרך.',
        icon: Users
    },
    {
        title: 'הבנת רגשות',
        text: 'לשחק דמות אחרת זה להבין איך היא מרגישה. הילדים לומדים לזהות רגשות, לבטא אותם ולגלות אמפתיה.',
        icon: Drama
    },
    {
        title: 'ריכוז והקשבה',
        text: 'לזכור טקסט, לחכות לתור ולהגיב בזמן הנכון. משחקי במה מחזקים קשב בדרך הכי כיפית שיש.',
        icon: Ear
    }
]

const BenefitsSection: FC = (): ReactNode => {
    return (
        <section id='why' aria-labelledby='why-title' className='scroll-mt-16 py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal className='max-w-2xl'>
                    <p className='text-sm font-medium text-primary'>
                        למה תיאטרון?
                    </p>

                    <h2 id='why-title' className='mt-3 font-heading text-4xl font-bold leading-tight text-balance sm:text-5xl'>
                        מה ילדים לוקחים איתם מהבמה
                    </h2>

                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        חוג תיאטרון הוא הרבה יותר מללמוד טקסט בעל פה. בכל שיעור הילדים מתנסים, מעזים ומגלים משהו חדש על עצמם ועל אחרים.
                    </p>
                </Reveal>

                <ul className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {BENEFITS.map((benefit, index) => {
                        const Icon = benefit.icon

                        return (
                            <li key={benefit.title}>
                                <Reveal delay={(index % 3) * 100} className='h-full'>
                                    <div className='h-full rounded-3xl border border-border bg-card/60 p-7 transition-colors duration-500 hover:border-primary/40'>
                                        <span className='flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
                                            <Icon aria-hidden='true' className='size-6' />
                                        </span>

                                        <h3 className='mt-5 font-heading text-2xl font-bold'>
                                            {benefit.title}
                                        </h3>

                                        <p className='mt-2.5 leading-relaxed text-muted-foreground text-pretty'>
                                            {benefit.text}
                                        </p>
                                    </div>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default BenefitsSection
